import { useQuery } from '@tanstack/react-query'

import React, {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  loadPaymentWidget,
  PaymentWidgetInstance,
} from '@tosspayments/payment-widget-sdk'

import { Controller, useForm } from 'react-hook-form'

import { useLocation } from 'react-router-dom'

import classNames from 'classnames'
import { UserInfoType } from '../../service/user'
import { AffiliatePoint } from '../../service/payment'
import { RequestPurchasePoint } from '../../service/point'
import useGetAutoChargeList from '../../hooks/query/point/useGetAutoChargeList'
import { useUpdateRequestPurchasePoint } from '../../hooks/query/point/useUpdateRequestPurchasePoint'

import { CircularCheckbox } from '../CircularCheckbox'
import { CASH_INFO_FIRST, CASH_REFUND_INFO } from '../../utils/common'
import { Modal } from '../layout/Modal'
import useGetPromotion from '../../hooks/query/point/useGetPromotion'
import { createLogger } from 'utils/logger'

interface ModalProps {
  setCashPaymentModal: Dispatch<SetStateAction<boolean>>
  userInfo: UserInfoType
  affiliatePoint?: AffiliatePoint
  userCurrentPoint: number
  requestPurchasePoint: RequestPurchasePoint
}

const logger = createLogger('CashPaymentModal')

export const CashPaymentModal = ({
  children,
  setCashPaymentModal,
  affiliatePoint,
  userInfo,
  userCurrentPoint,
  requestPurchasePoint,
}: PropsWithChildren<ModalProps>) => {
  const { pathname } = useLocation()
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const close = params.get('close')
  const selectType = params.get('selectType')

  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      pointAmount: 0,
      pointId: 0,
      pointGroup: 'popular',
      bonus: 0,
    },
  })

  const { data: autoChargeList } = useGetAutoChargeList()
  //const { data: pointListData, refetch: pointListRefetch } = useGetPointList()
  const { mutate: updateMutate } = useUpdateRequestPurchasePoint()
  const [currentPoint, setCurrentPoint] = useState(userCurrentPoint)

  const [promotionInfo, setPromotionInfo] = useState({
    point: 0,
    pointRate: 0,
    expired: '',
    expiredDay: 0,
  })
  const [totalPrice, setTotalPrice] = useState(33000)
  const [selectPoint, setSelectPoint] = useState(30000)
  const [bonusValue, setBonusValue] = useState(0)
  const [selectPointVat, setSelectPointVat] = useState(0)

  const [promotionCode, setPromotionCode] = useState<string>('')
  const [isValidCode, setValidCode] = useState<boolean>(false)
  const [isAffiliateBenefit, setAffiliateBenefit] = useState<boolean>(false)
  const [isUseInviteCode, setUseInviteCode] = useState<boolean>(false)
  const clientKey = process.env.REACT_APP_KEY ?? ''
  const [isModal, setModal] = useState<boolean>(false)
  const [errorText, setErrorText] = useState({
    title: '',
    subtitle: <></>,
  })
  const [isChecked, setIsChecked] = useState([false, false])

  const handleChange = (event: any, index: number) => {
    const updateChecked = isChecked.map((el, i) => (i === index ? !el : el))
    setIsChecked(updateChecked)
  }

  function handlePathName(pathname: string) {
    let newPath = pathname
    if (pathname === '/shortform-price-guide') {
      newPath = '/my-cash-dashboard'
    }
    return newPath
  }
  function usePaymentWidget(clientKey: string, customerKey: string) {
    return useQuery({
      queryKey: ['payment-widget', clientKey, customerKey],
      queryFn: () => {
        return loadPaymentWidget(clientKey, customerKey)
      },
    })
  }
  const { mutate, isPending } = useGetPromotion()
  const { data: paymentWidget } = usePaymentWidget(
    clientKey,
    requestPurchasePoint?.orderId,
  )
  const paymentWidgetRef = useRef<PaymentWidgetInstance | null>(null)
  const paymentMethodsWidgetRef = useRef<ReturnType<
    PaymentWidgetInstance['renderPaymentMethods']
  > | null>(null)

  const handelPayment = () => {
    if (isValidCode || isAffiliateBenefit) {
      requestPurchasePoint.promotionCode = promotionCode
    }
    const newPath = handlePathName(pathname)
    updateMutate(requestPurchasePoint, {
      onSuccess: () => {
        const paymentWidget = paymentWidgetRef.current

        try {
          if (requestPurchasePoint?.orderId) {
            paymentWidget?.requestPayment({
              orderId: requestPurchasePoint?.orderId,
              orderName: `패스트컷 포인트 (${selectPoint.toLocaleString()}원)`,
              customerName: userInfo.name,
              customerEmail: userInfo.email,
              successUrl: `${window.location.origin}/payment-success?promotionCode=${promotionInfo.pointRate}&valid=${isValidCode}&bonus=${bonusValue}&type=point&close=${close}&selectType=${selectType}`,
              failUrl: `${window.location.origin}/payment-fail?path=${newPath}&type=point`,
            })
          }
        } catch (err) {
          logger.error(JSON.stringify(err))
        }
      },
      onError: () => {},
    })
  }

  const handleInvalidCode = () => {
    if (!isAffiliateBenefit) {
      setUseInviteCode(false)
      setPromotionCode('')
      setValidCode(false)
    }

    //가격도 원래 가격으로 다시 돌려야함
  }
  const handlePromotion = () => {
    const data = promotionCode
    mutate(data, {
      onSuccess: (data) => {
        setPromotionInfo(data)
        setUseInviteCode(true)
        setValidCode(true)
      },
      onError: (e) => {
        logger.error(JSON.stringify(e))
        const error: any = e
        const errorCode = error?.response?.data?.message
        setValidCode(false)
        if (errorCode === 450) {
          setErrorText({
            title: '💁🏻 할인 코드 오류',
            subtitle: (
              <>
                입력하신 할인 코드는 존재하지 않는 코드입니다. <br />
                코드를 다시 잘 확인해서 입력해주세요!
              </>
            ),
          })
        } else if (errorCode === 460) {
          setErrorText({
            title: '🗓 할인 코드 기한 만료',
            subtitle: (
              <>
                입력하신 할인 코드는 사용 기한이 만료되었습니다.
                <br />
                다른 할인 코드를 입력해주세요!
              </>
            ),
          })
        }
        setModal((prev) => !prev)
        logger.error(JSON.stringify(e))
      },
    })
  }

  const calculatePromotion = (amount: number) => {
    const { pointRate } = promotionInfo

    return `할인 코드 ${pointRate}%`
  }

  const calculateFinalPoint = (amount: number) => {
    const { pointRate } = promotionInfo
    //선택된 amount에 pointRate만큼 주기

    let calculateCash = amount * (pointRate / 100)
    return ` + ${pointRate}%(${calculateCash.toLocaleString()} 캐시)`
  }

  useEffect(() => {
    ;(async () => {
      if (requestPurchasePoint?.orderId) {
        const paymentWidget = await loadPaymentWidget(
          clientKey,
          requestPurchasePoint?.orderId,
        )
        const paymentMethodsWidget = paymentWidget.renderPaymentMethods(
          '#payment-widget',
          totalPrice,
          { variantKey: 'cashOnly' },
        )

        paymentWidgetRef.current = paymentWidget
        paymentMethodsWidgetRef.current = paymentMethodsWidget
      }
    })()
  }, [])

  useEffect(() => {
    const pointAmount = watch('pointAmount')
    const bonus = watch('bonus')
    setBonusValue(bonus)
    setSelectPoint(pointAmount)
    setSelectPointVat(pointAmount * 0.1)
    setTotalPrice(pointAmount + pointAmount * 0.1)
    requestPurchasePoint.amount = pointAmount
    requestPurchasePoint.price = pointAmount
    requestPurchasePoint.vat = pointAmount * 0.1
    requestPurchasePoint.productName = `패스트컷 포인트 (${pointAmount.toLocaleString()}원)`
    requestPurchasePoint.chargeOptionId = watch('pointId')
  }, [watch('pointAmount')])

  useEffect(() => {
    const paymentMethodsWidget = paymentMethodsWidgetRef.current

    if (paymentMethodsWidget == null) {
      return
    }

    paymentMethodsWidget.updateAmount(
      totalPrice,
      paymentMethodsWidget.UPDATE_REASON.POINT,
    )
  }, [totalPrice])

  useEffect(() => {
    if (autoChargeList?.benefitInfo?.affiliateDescription) {
      setAffiliateBenefit(true)
    }
    if (autoChargeList?.chargeMap) {
      setValue('bonus', autoChargeList.chargeMap.popular[0].bonus)
      setValue('pointGroup', 'popular')
      setValue('pointId', autoChargeList.chargeMap.popular[0].id)
      setValue(
        'pointAmount',
        Number(autoChargeList.chargeMap.popular[0].chargeAmount),
      )
      if (affiliatePoint?.affiliateCode)
        setPromotionCode(affiliatePoint?.affiliateCode)
    }
  }, [autoChargeList])

  return (
    <div className="fixed left-0 top-0 z-50 flex h-screen w-full flex-col items-center justify-center bg-black bg-opacity-50 text-center md:p-[40px]">
      <div className="w-full overflow-y-auto border bg-white px-[20px] pb-[100px] md:w-[600px] md:rounded-[20px] md:px-[40px]">
        <div className="sticky top-0 z-10 bg-white pb-5 pt-10">
          <p className="relative flex items-center justify-center">
            <p className="text-[24px] font-bold md:text-[32px]">캐시 충전!</p>
            <button
              onClick={() => {
                if (close) {
                  window.close()
                }
                setCashPaymentModal(false)
              }}
              className="absolute right-0"
            >
              <img
                src="/assets/img/Icon_x.png"
                alt="icon_x"
                height={40}
                width={40}
              />
            </button>
          </p>
        </div>
        <div className="mb-[8px] h-auto rounded-[20px] bg-[#F6F8FB] p-[20px] md:mt-[32px] md:px-[32px] md:py-[24px]">
          <div className="flex justify-between text-[20px] font-bold">
            <p>보유 캐시</p>
            <p>{currentPoint.toLocaleString()}캐시</p>
          </div>
        </div>
        <div className="mb-[8px] h-auto rounded-[20px] bg-[#F6F8FB] p-[20px] md:px-[32px] md:py-[24px]">
          <div className="flex flex-col">
            <div className="flex justify-between text-[20px] font-bold">
              <p>할인 코드</p>
            </div>
            <div className="relative mt-[16px]">
              <input
                className="h-[75px] w-full rounded-[20px] border-[1px] p-5 px-5 md:h-[83px] md:p-[16px] md:px-[32px]"
                placeholder="할인 코드를 입력"
                onChange={(e) => {
                  setPromotionCode(e.target.value)
                }}
                value={isAffiliateBenefit ? '' : promotionCode}
                disabled={isValidCode || isAffiliateBenefit}
              />
              <div className="absolute bottom-4 right-3 flex items-center gap-[16px] md:top-3">
                <button onClick={handleInvalidCode}>
                  <img
                    className="h-[24px] w-[24px] md:h-[32px] md:w-[32px]"
                    src="/assets/img/icon_circle_x.png"
                    alt="x"
                  />
                </button>
                <button
                  className="flex items-center justify-center rounded-[10px] bg-[#6B8ACB] px-[20px] py-[10px] font-bold text-white hover:bg-[#8FABE5] active:scale-95 disabled:opacity-[0.3] md:h-[51px] md:w-[76px] md:px-[24px] md:py-[16px]"
                  onClick={handlePromotion}
                  disabled={!promotionCode.length || isValidCode}
                >
                  적용
                </button>
              </div>
            </div>
          </div>
        </div>
        <form>
          <div className="flex h-auto flex-col gap-[16px] rounded-[20px] bg-[#F6F8FB] p-[20px] md:p-[32px]">
            <p className="text-left text-[20px] font-bold">충전 단위 선택</p>
            <div className="popular-list flex flex-col rounded-[10px] bg-white p-[20px] text-[14px] md:w-[456px] md:text-[18px]">
              <div className="mb-5 flex">
                <span className="rounded-[4px] bg-main px-[12px] py-[4px] text-[14px] font-bold text-white md:text-[18px]">
                  인기
                </span>
              </div>
              {autoChargeList?.chargeMap?.popular?.map((el, index) => (
                <>
                  <div className="flex items-center justify-between gap-[8px]">
                    <Controller
                      render={({ field }) => (
                        <CircularCheckbox
                          {...field}
                          id={index}
                          checked={
                            watch('pointAmount') === Number(el.chargeAmount) &&
                            watch('pointId') === el.id &&
                            watch('pointGroup') === 'popular'
                          }
                          onChange={() => {
                            setValue('pointAmount', Number(el.chargeAmount))
                            setValue('pointId', el.id)
                            setValue('pointGroup', 'popular')
                            setValue('bonus', el.bonus)
                          }}
                          className="font-bold"
                          //disabled={!userInfo || !thumbnail.usable}

                          label={`${Number(
                            el.chargeAmount,
                          ).toLocaleString()}캐시`}
                        />
                      )}
                      name="pointAmount"
                      control={control}
                    />

                    <div className="flex flex-col items-end text-[12px] md:text-[18px]">
                      {Number(el.bonusRate) > 0 && (
                        <>
                          <p className="font-bold">
                            {' '}
                            + {`${el.bonus.toLocaleString()}`} 캐시 보너스
                          </p>
                          <p
                            className={classNames(
                              'text-[12px] md:text-[14px]',
                              {
                                'mb-4': isValidCode,
                              },
                            )}
                          >
                            {autoChargeList.benefitInfo?.affiliateDescription &&
                              '제휴 혜택'}
                            {autoChargeList.benefitInfo?.inviteCode &&
                              '초대 코드'}
                            {autoChargeList.benefitType === 'purchase' &&
                              '충전 혜택'}
                            {` `}
                            <span
                              className={classNames(
                                'rounded-[4px] px-1 font-bold',
                                {
                                  'bg-[#EEF1F7] text-error':
                                    Number(el.bonusRate) <= 9,
                                  'bg-error text-white':
                                    Number(el.bonusRate) > 9,
                                },
                              )}
                            >
                              {`${Number(el.bonusRate).toFixed(0)}%`}
                            </span>
                          </p>
                        </>
                      )}
                      {isValidCode && (
                        <div className="flex justify-end rounded-[2px] bg-[#FFF4F4] p-1 text-[12px] text-error md:text-[14px]">
                          {calculatePromotion(Number(el.chargeAmount))}
                        </div>
                      )}
                    </div>
                  </div>
                  {index !== autoChargeList.chargeMap?.popular.length - 1 && (
                    <hr className="my-[20px] h-[1px] bg-[#EEF1F7]" />
                  )}
                </>
              ))}
            </div>
            <div className="list flex flex-col rounded-[10px] bg-white p-[20px] text-[14px] md:w-[456px] md:text-[18px]">
              <div className="mb-5 flex">
                <span className="rounded-[4px] bg-[#8F929B] px-[12px] py-[4px] text-[14px] font-bold text-white md:text-[18px]">
                  전체
                </span>
              </div>
              {autoChargeList?.chargeMap?.total?.map((el, index) => (
                <>
                  <div className="flex items-center justify-between gap-[8px]">
                    <Controller
                      render={({ field }) => (
                        <>
                          {' '}
                          <CircularCheckbox
                            {...field}
                            id={index}
                            checked={
                              watch('pointAmount') ===
                                Number(el.chargeAmount) &&
                              watch('pointId') === el.id &&
                              watch('pointGroup') === 'total'
                            }
                            onChange={() => {
                              setValue('pointAmount', Number(el.chargeAmount))
                              setValue('pointId', el.id)
                              setValue('pointGroup', 'total')
                              setValue('bonus', el.bonus)
                            }}
                            label={`${Number(
                              el.chargeAmount,
                            ).toLocaleString()}캐시`}
                            className="font-bold"
                          />
                        </>
                      )}
                      name="pointAmount"
                      control={control}
                    />
                    <div className="flex flex-col items-end text-[12px] md:text-[18px]">
                      {Number(el.bonusRate) > 0 && (
                        <>
                          <p className="font-bold">
                            {' '}
                            + {`${el.bonus.toLocaleString()}`} 캐시 보너스
                          </p>
                          <p
                            className={classNames(
                              'text-[12px] md:text-[14px]',
                              {
                                'mb-4': isValidCode,
                              },
                            )}
                          >
                            {autoChargeList.benefitInfo?.affiliateDescription
                              ? '제휴 혜택'
                              : '충전 혜택'}{' '}
                            <span
                              className={classNames(
                                'rounded-[4px] px-1 font-bold',
                                {
                                  'bg-[#EEF1F7] text-error':
                                    Number(el.bonusRate) <= 9,
                                  'bg-error text-white':
                                    Number(el.bonusRate) > 9,
                                },
                              )}
                            >
                              {`${Number(el.bonusRate).toFixed(0)}%`}
                            </span>
                          </p>
                        </>
                      )}
                      {isValidCode && (
                        <div className="flex justify-end bg-[#FFF4F4] p-1 text-[14px] text-error">
                          {calculatePromotion(Number(el.chargeAmount))}
                        </div>
                      )}
                    </div>
                  </div>
                  {index !== autoChargeList.chargeMap?.total.length - 1 && (
                    <hr className="my-[20px] h-[1px] bg-[#EEF1F7]" />
                  )}
                </>
              ))}
            </div>
            <div className="after-point flex justify-between rounded-[10px] bg-white p-[20px] text-base font-bold md:w-[456px] md:text-[24px]">
              <p>충전 예정 캐시</p>
              <div className="flex flex-col items-end">
                <p className="text-[20px] md:text-[24px]">
                  {(selectPoint + bonusValue).toLocaleString()}캐시
                </p>
                {isValidCode && (
                  <p className="mt-2 text-base text-error md:text-[18px]">
                    {calculateFinalPoint(selectPoint)}
                  </p>
                )}
              </div>
            </div>
          </div>
        </form>

        <div className="mt-[24px] flex flex-col">
          <p className="w-full text-left text-[24px] font-bold">금액 안내</p>
          <div className="point mt-[16px] flex flex-col rounded-[20px] bg-[#F6F8FB] p-[20px] md:px-[32px] md:py-[24px]">
            <div className="flex items-center justify-between">
              <p className="text-[18px] font-bold md:text-[20px]">캐시</p>
              <p className="text-[18px] font-bold md:text-[20px]">
                {' '}
                {selectPoint.toLocaleString()}원
              </p>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-[18px] font-bold md:text-[20px]">
                VAT(부가세10%)
              </p>
              <p className="text-[18px] font-bold md:text-[20px]">
                {' '}
                {(selectPoint * 0.1).toLocaleString()}원
              </p>
            </div>
          </div>
        </div>

        <div className="mt-[8px] flex h-[78px] items-center justify-between rounded-[20px] bg-[#AFECEC] p-[20px] md:flex-row md:px-[32px] md:py-[24px]">
          <p className="text-[18px] font-bold md:text-[24px]">총 결제금액</p>
          <p className="text-[24px] font-bold">
            {' '}
            {(selectPoint + selectPoint * 0.1).toLocaleString()}원
          </p>
        </div>

        <div className="box_section w-full">
          <div id="payment-widget" />
        </div>

        <div className="flex w-full flex-col items-start justify-start gap-[12px] rounded-[20px] border-[2px] border-[#D9DEE8] bg-[#F6F8FB] p-[20px]">
          <div className="flex flex-col items-start justify-start">
            <p className="p-[10px] text-[18px] font-bold">패스트컷 캐시 안내</p>
            <ul className="list-disc pl-8 text-left">
              {CASH_INFO_FIRST.map((el) => {
                return (
                  <>
                    <li>{el}</li>
                  </>
                )
              })}
            </ul>
          </div>
          <div className="flex flex-col items-start justify-start">
            <p className="p-[10px] text-[18px] font-bold">
              패스트컷 캐시 환불규정
            </p>
            <ul className="list-disc pl-8 text-left">
              {Object.entries(CASH_REFUND_INFO).map((el) => {
                const [number, info] = el
                return (
                  <>
                    <li>{info.text}</li>
                    {info?.firstSubText && (
                      <ul className="list-circle pl-4 text-left">
                        <li>{info.firstSubText}</li>
                      </ul>
                    )}
                  </>
                )
              })}

              <li>
                그 밖의 상세 규정은{' '}
                <span>
                  <a
                    className="text-href underline"
                    href="https://angelleague.notion.site/f073ce324b194809b89b07fb4baf4811"
                    target="_blank"
                    rel="noreferrer"
                  >
                    ‘패스트컷 캐시정책’
                  </a>
                </span>
                을 따릅니다.
              </li>
            </ul>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center">
          <div className="mt-[24px] flex w-full items-center gap-3 md:w-[430px]">
            <input
              type="checkbox"
              checked={isChecked[0]}
              onChange={(e) => {
                handleChange(e, 0)
              }}
              className="h-[20px] w-[20px] shrink-0"
            />
            <p className="text-left text-[18px] font-[500] text-[#8F929B]">
              [필수]{' '}
              <span>
                <a
                  className="text-href underline"
                  href="https://angelleague.notion.site/f073ce324b194809b89b07fb4baf4811"
                  target="_blank"
                  rel="noreferrer"
                >
                  패스트컷 캐시정책
                </a>
              </span>{' '}
              및 위 환불규정에 따릅니다
            </p>
          </div>
          <div className="mt-[12px] flex w-full items-center gap-3 md:w-[430px]">
            <input
              type="checkbox"
              checked={isChecked[1]}
              onChange={(e) => {
                handleChange(e, 1)
              }}
              className="h-[20px] w-[20px]"
            />
            <p className="text-left text-[18px] font-[500] text-[#8F929B]">
              [필수] 만 14세 이상입니다
            </p>
          </div>
        </div>
        <div className="my-[24px] flex h-[60px] w-full items-center justify-center rounded-[40px] bg-main text-[18px] font-bold text-[white]">
          <button
            className="h-full w-full"
            disabled={!isChecked.every((element) => element)}
            onClick={() => {
              handelPayment()
            }}
            type="button"
          >
            {totalPrice.toLocaleString()}원 결제하기
          </button>
        </div>
      </div>
      {isModal && (
        <Modal isModal={isModal}>
          <div className="h-[184px] w-[400px] rounded-t-[20px] border-t bg-white p-[20px]">
            <div className="flex h-full flex-col items-center justify-center gap-[24px]">
              <p className="text-[18px] font-bold">{errorText.title}</p>
              <div className="flex flex-col leading-[22px]">
                <p className="text-[14px]">{errorText.subtitle}</p>
              </div>
            </div>
          </div>
          <div className="flex h-[60px] w-[400px] items-center justify-center text-[14px]">
            <button
              className="h-full w-[100%] rounded-b-[20px] border-b border-main bg-main font-bold text-white"
              onClick={() => {
                setModal((prev) => !prev)
              }}
            >
              확인
            </button>
          </div>
        </Modal>
      )}
    </div>
  )
}
